import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, LinearProgress } from '@mui/material';

/**
 * DiffRenderer - Renders paginated diff from backend with infinite scroll
 *
 * Props:
 * - diff: array of {type, old_line, new_line, content}
 * - loading: show loading state
 * - loadingMore: show loading more indicator
 * - onScroll: callback with {startIndex, endIndex} when scrolling
 * - onLoadMore: callback to load more lines
 * - scrollToIndex: line index to scroll to
 * - height: container height (default 500)
 * - hasMore: boolean if there are more lines to load
 * - totalLines: total number of lines
 * - highlightIndex: line index to highlight (from search)
 * - searchQuery: current search query (for highlighting)
 */
export default function DiffRenderer({
  diff = [],
  loading = false,
  loadingMore = false,
  onScroll,
  onLoadMore,
  scrollToIndex,
  height = 500,
  hasMore = false,
  totalLines = 0,
  highlightIndex,
  searchQuery = ''
}) {
  const containerRef = useRef(null);
  const lineRefs = useRef({});
  const loadMoreTriggerRef = useRef(null);

  const LINE_HEIGHT = 24;

  const COLORS = {
    addBg: '#e6ffec',
    addGutter: '#ccffd8',
    addText: '#1a7f37',
    removeBg: '#ffebe9',
    removeGutter: '#ffc1c0',
    removeText: '#cf222e',
    equalBg: '#fafafa',
    equalGutter: '#f0f0f0',
    gutterText: '#666',
    highlightBg: '#fff3cd',
    highlightBorder: '#ffc107',
  };

  // Scroll to index when prop changes
  useEffect(() => {
    if (scrollToIndex !== undefined && scrollToIndex >= 0 && lineRefs.current[scrollToIndex]) {
      lineRefs.current[scrollToIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollToIndex]);

  // Infinite scroll - load more when reaching bottom
  useEffect(() => {
    if (!hasMore || loadingMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  // Handle scroll events for minimap sync
  const handleScroll = useCallback(() => {
    if (!onScroll || !containerRef.current) return;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;

    const startIndex = Math.floor(scrollTop / LINE_HEIGHT);
    const endIndex = Math.min(startIndex + Math.ceil(clientHeight / LINE_HEIGHT), diff.length);

    onScroll({ startIndex, endIndex });
  }, [onScroll, diff.length]);

  // Highlight search term in content
  const highlightContent = (content, query) => {
    if (!query || !content) return content;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = content.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={{ backgroundColor: '#ffc107', fontWeight: 'bold' }}>{part}</span>
      ) : (
        part
      )
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: height,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Carregando diferenças...</Typography>
      </Box>
    );
  }

  if (diff.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: height,
        }}
      >
        <Typography color="text.secondary">Nenhuma diferença encontrada</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: '#fafafa',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          py: 0.5,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ width: 50, textAlign: 'center', fontSize: '11px', color: 'text.secondary', fontWeight: 'bold' }}>
          Ant.
        </Box>
        <Box sx={{ width: 50, textAlign: 'center', fontSize: '11px', color: 'text.secondary', fontWeight: 'bold' }}>
          Atual
        </Box>
        <Box sx={{ width: 20 }} />
        <Box sx={{ flex: 1, fontSize: '11px', color: 'text.secondary', px: 1 }}>
          Conteúdo {totalLines > 0 && `(${diff.length}/${totalLines} linhas)`}
        </Box>
      </Box>

      {/* Loading more indicator at top */}
      {loadingMore && <LinearProgress />}

      {/* Diff lines */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          height: typeof height === 'string' ? `calc(${height} - 30px)` : height - 30,
          overflow: 'auto',
        }}
      >
        {diff.map((line, index) => {
          let bgColor, gutterColor, textColor, symbol;
          const isHighlighted = highlightIndex === index;

          switch (line.type) {
            case 'add':
              bgColor = isHighlighted ? COLORS.highlightBg : COLORS.addBg;
              gutterColor = COLORS.addGutter;
              textColor = COLORS.addText;
              symbol = '+';
              break;
            case 'remove':
              bgColor = isHighlighted ? COLORS.highlightBg : COLORS.removeBg;
              gutterColor = COLORS.removeGutter;
              textColor = COLORS.removeText;
              symbol = '-';
              break;
            default:
              bgColor = isHighlighted ? COLORS.highlightBg : COLORS.equalBg;
              gutterColor = COLORS.equalGutter;
              textColor = 'inherit';
              symbol = ' ';
          }

          return (
            <Box
              key={index}
              ref={el => lineRefs.current[index] = el}
              sx={{
                display: 'flex',
                bgcolor: bgColor,
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                minHeight: LINE_HEIGHT,
                borderLeft: isHighlighted ? `3px solid ${COLORS.highlightBorder}` : 'none',
                '&:hover': {
                  bgcolor: line.type === 'equal' && !isHighlighted ? '#f0f0f0' : undefined,
                }
              }}
            >
              {/* Old line number gutter */}
              <Box
                sx={{
                  width: 50,
                  minWidth: 50,
                  bgcolor: gutterColor,
                  color: COLORS.gutterText,
                  textAlign: 'right',
                  pr: 1,
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontSize: '12px',
                  lineHeight: `${LINE_HEIGHT}px`,
                  userSelect: 'none',
                  borderRight: '1px solid rgba(0,0,0,0.1)',
                }}
              >
                {line.old_line || ''}
              </Box>

              {/* New line number gutter */}
              <Box
                sx={{
                  width: 50,
                  minWidth: 50,
                  bgcolor: gutterColor,
                  color: COLORS.gutterText,
                  textAlign: 'right',
                  pr: 1,
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontSize: '12px',
                  lineHeight: `${LINE_HEIGHT}px`,
                  userSelect: 'none',
                  borderRight: '1px solid rgba(0,0,0,0.1)',
                }}
              >
                {line.new_line || ''}
              </Box>

              {/* Change symbol */}
              <Box
                sx={{
                  width: 20,
                  minWidth: 20,
                  textAlign: 'center',
                  color: textColor,
                  fontWeight: 'bold',
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontSize: '13px',
                  lineHeight: `${LINE_HEIGHT}px`,
                }}
              >
                {symbol}
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  whiteSpace: 'pre',
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontSize: '13px',
                  lineHeight: `${LINE_HEIGHT}px`,
                  color: textColor,
                  px: 1,
                }}
              >
                {searchQuery ? highlightContent(line.content, searchQuery) : line.content}
              </Box>
            </Box>
          );
        })}

        {/* Load more trigger */}
        {hasMore && (
          <Box
            ref={loadMoreTriggerRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              color: 'text.secondary',
            }}
          >
            {loadingMore ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="body2">Carregando mais...</Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
